import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';
import {
  GET_INITIATIVE_ROLES_LIST,
  GET_INITIATIVE_SHIFTS_LIST,
  GET_INITIATIVE_USHER_DETAILS,
  GET_LOCATION_DETAILS,
} from 'app/graphql/queries';

import { AddInitiativeUshersStepResult } from 'app/redux/actions/InitiativeUsher';

import dayjs from 'dayjs';
import moment from 'moment';

import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  TimePicker,
  Typography,
} from 'antd';

import { Loading } from 'app/components/Loading';

import ResultsFormStyles from './ResultsFormStyles';

const { Title } = Typography;
const { Option } = Select;

const AddResultsForm = () => {
  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const decision = Form.useWatch('decision', form);
  const applyReasons = Form.useWatch('apply_reasons', form);

  const { initiativeUsherID, initiativeUsherStepID } = useParams();

  const { loading: registrationDetailsLoading, data: registrationDetailsData } =
    useQuery(GET_INITIATIVE_USHER_DETAILS, {
      variables: { initiativeUsherId: initiativeUsherID },
    });

  const registrationData =
    registrationDetailsData?.initiativeUshers?.edges[0]?.node;

  const { loading: locationDetailsLoading, data: locationDetails } = useQuery(
    GET_LOCATION_DETAILS,
    {
      variables: { initiativeId: parseInt(registrationData?.initiative?.pk) },
      skip: !registrationData || decision !== 'passed',
    },
  );

  const { loading: shiftsListLoading, data: shiftsListData } = useQuery(
    GET_INITIATIVE_SHIFTS_LIST,
    {
      variables: { initiativeId: parseInt(registrationData?.initiative?.pk) },
      skip: !registrationData || decision !== 'passed',
    },
  );

  const { loading: rolesListLoading, data: rolesListData } = useQuery(
    GET_INITIATIVE_ROLES_LIST,
    {
      variables: { initiativeId: parseInt(registrationData?.initiative?.pk) },
      skip: !registrationData || decision !== 'passed',
    },
  );

  const onFinish = async values => {
    const applyReasonsArray = values.apply_reasons.map(reason =>
      reason === 'other' && values.apply_reasons_other
        ? values.apply_reasons_other
        : reason,
    );

    const data = {
      ...values,
      apply_reasons: applyReasonsArray.join(', '),
      interview_time: dayjs(values.interview_time).format('HH:mm'),
      interview_date: dayjs(values.interview_date).format('YYYY-MM-DD'),
      step: initiativeUsherStepID,
    };

    console.log('Form Values:', data);
    await dispatch(AddInitiativeUshersStepResult(data));
  };

  if (registrationDetailsLoading) return <Loading />;

  return (
    <ResultsFormStyles>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Results Form
      </Title>

      <Form
        form={form}
        onFinish={onFinish}
        scrollToFirstError={true}
        title="Results Form"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="General Information" className="mb-3">
              <Row gutter={[8, 8]}>
                <Col lg={6} md={12} xs={24}>
                  <Form.Item
                    label="Usher ID"
                    name="usher_id"
                    initialValue={registrationData.usher.usherId}
                    rules={[{ required: true }]}
                  >
                    <Input readOnly disabled />
                  </Form.Item>
                </Col>

                <Col lg={6} md={12} xs={24}>
                  <Form.Item
                    label="Usher Name"
                    name="usher_name"
                    initialValue={registrationData.usher.englishFullName}
                    rules={[{ required: true }]}
                  >
                    <Input readOnly disabled />
                  </Form.Item>
                </Col>

                <Col lg={12} md={24} xs={24}>
                  <Form.Item
                    label="Initiative"
                    name="initiative"
                    initialValue={
                      registrationData.initiative.englishName +
                      ' - ' +
                      moment(registrationData.initiative.startingDate).format(
                        'DD/MM/YYYY',
                      )
                    }
                    rules={[{ required: true }]}
                  >
                    <Input readOnly disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col lg={8} md={12} xs={24}>
                  <Form.Item
                    label="Interview Date"
                    name="interview_date"
                    initialValue={dayjs()}
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
                  </Form.Item>
                </Col>

                <Col lg={8} md={12} xs={24}>
                  <Form.Item
                    label="Interview Time"
                    name="interview_time"
                    initialValue={dayjs()}
                    rules={[{ required: true }]}
                  >
                    <TimePicker
                      style={{ width: '100%' }}
                      format="HH:mm"
                      use12Hours={true}
                    />
                  </Form.Item>
                </Col>

                <Col lg={8} md={12} xs={24}>
                  <Form.Item
                    label="Assessor Name"
                    name="assessor_name"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col xs={24}>
                  <Form.Item label="Comments" name="general_comments">
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="Interviewer Section" className="mb-3">
              <Row gutter={[8, 8]}>
                <Col xs={24}>
                  <Form.Item
                    label="English Level"
                    name="english_level"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group>
                      <Radio value="poor">Poor (25%)</Radio>
                      <Radio value="average">Average (50%)</Radio>
                      <Radio value="very_good">Very Good (75%)</Radio>
                      <Radio value="excellent">Excellent (100%)</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col xs={24}>
                  <Form.Item
                    label="Arabic Level"
                    name="arabic_level"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group>
                      <Radio value="poor">Poor (25%)</Radio>
                      <Radio value="average">Average (50%)</Radio>
                      <Radio value="very_good">Very Good (75%)</Radio>
                      <Radio value="excellent">Excellent (100%)</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col md={12} xs={24}>
                  <Form.Item
                    label="Work Experience"
                    name="work_experience"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group>
                      <Radio value="no">No Experience</Radio>
                      <Radio value="regular">Regular Experience</Radio>
                      <Radio value="remarkable">Remarkable Experience</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>

                <Col md={12} xs={24}>
                  <Form.Item label="Comments" name="work_experience_comments">
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col md={12} xs={24}>
                  <Form.Item
                    label="Volunteering Experience"
                    name="volunteering_experience"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group>
                      <Radio value="no">No Experience</Radio>
                      <Radio value="regular">Regular Experience</Radio>
                      <Radio value="remarkable">Remarkable Experience</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>

                <Col md={12} xs={24}>
                  <Form.Item
                    label="Comments"
                    name="volunteering_experience_comments"
                  >
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col xs={24}>
                  <Form.Item
                    label="Why did you apply for this program?"
                    name="apply_reasons"
                    rules={[{ required: true }]}
                  >
                    <Checkbox.Group style={verticalGroupStyle}>
                      <Checkbox value="waiting_job">Waiting for a Job</Checkbox>
                      <Checkbox value="Skill Development">
                        Skill Development
                      </Checkbox>
                      <Checkbox value="Additional Income">
                        Additional Income
                      </Checkbox>
                      <Checkbox value="Interested in Organizing Events">
                        Interested in Organizing Events
                      </Checkbox>
                      <Checkbox value="Socializing & Networking">
                        Socializing & Networking
                      </Checkbox>
                      <Checkbox value="Joining with friends/family">
                        Joining with friends/family
                      </Checkbox>
                      <Checkbox value="other">
                        Other
                        {applyReasons?.includes('other') && (
                          <Form.Item
                            name="apply_reasons_other"
                            rules={[{ required: true }]}
                            noStyle
                          >
                            <Input
                              placeholder="please specify"
                              style={{ width: 600, marginInlineStart: 6 }}
                            />
                          </Form.Item>
                        )}
                      </Checkbox>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[8, 8]}>
                <Col xs={24}>
                  <Form.Item
                    label="Comments"
                    name="interviewer_section_comments"
                  >
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="Personal Skills" className="mb-3">
              {[
                {
                  label: 'Dealing with Kids',
                  name: 'dealing_with_kids',
                  extra: 'Do you like dealing with kids?',
                },
                {
                  label: 'Leadership Skills',
                  name: 'leadership_skills',
                  extra: 'Are you a team player or a leader, why?',
                },
                {
                  label: 'Work Under Stressful Situations',
                  name: 'stressful_situations',
                  extra: 'Give one example',
                },
                {
                  label: 'Social Intelligence',
                  name: 'social_intelligence',
                  extra:
                    'If an angry visitor is yelling at you, what would you do?',
                },
                {
                  label: 'If your leader is not fair',
                  name: 'unfair_leader',
                  extra:
                    'example: change of location, change of off day, warnings',
                },
              ].map((item, index) => (
                <Row gutter={[8, 8]} key={index}>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label={item.label}
                      name={`${item.name}_rating`}
                      rules={[
                        { required: true, message: 'Please select an option' },
                      ]}
                      extra={item.extra}
                    >
                      <Radio.Group>
                        <Radio value="unacceptable">Unacceptable</Radio>
                        <Radio value="satisfactory">Satisfactory</Radio>
                        <Radio value="exceptional">Exceptional</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item name={`${item.name}_comments`} noStyle>
                      <Input
                        placeholder="Comments"
                        style={{ marginTop: '8px' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="Usher Experience Evaluation" className="mb-3">
              {[
                {
                  label: 'Confidence while speaking',
                  name: 'confidence',
                },
                {
                  label: 'Enthusiasm while speaking',
                  name: 'enthusiasm',
                },
                {
                  label: 'Appropriate and varied volume',
                  name: 'speaking_volume',
                },
                {
                  label: 'Good use of eye contact',
                  name: 'eye_contact',
                },
              ].map((item, index) => (
                <Row gutter={[8, 8]} key={index}>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label={item.label}
                      name={`${item.name}_rating`}
                      rules={[
                        { required: true, message: 'Please select an option' },
                      ]}
                    >
                      <Radio.Group>
                        <Radio value="poor">Poor</Radio>
                        <Radio value="average">Average</Radio>
                        <Radio value="good">Good</Radio>
                        <Radio value="excellent">Excellent</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item name={`${item.name}_comments`} noStyle>
                      <Input
                        placeholder="Comments"
                        style={{ marginTop: '8px' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="Decision & Final Comments" className="mb-3">
              <Row gutter={[8, 8]}>
                <Col xs={24}>
                  <Form.Item
                    label="Decision"
                    name="decision"
                    rules={[{ required: true }]}
                  >
                    <Radio.Group>
                      <Radio value="passed">Passed</Radio>
                      <Radio value="failed">Failed</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              {decision === 'passed' && (
                <Row gutter={[8, 8]}>
                  <Col xs={24}>
                    <Form.Item
                      label="Rate"
                      name="rate"
                      rules={[{ required: true }]}
                    >
                      <Radio.Group>
                        <Radio value="rate1">Rate 1</Radio>
                        <Radio value="rate2">Rate 2</Radio>
                        <Radio value="backup">Backup</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={[8, 8]}>
                <Col xs={24}>
                  <Form.Item label="Final Comments" name="final_comments">
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {decision === 'passed' && (
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card title="Allocation Details" className="mb-3">
                <Row gutter={[8, 8]}>
                  <Col xs={24}>
                    <Form.Item
                      label="Assigned Location"
                      name="assigned_location"
                      rules={[{ required: true }]}
                      initialValue=""
                    >
                      <Select>
                        <Option key="" value="">
                          -----
                        </Option>
                        {!locationDetailsLoading &&
                          locationDetails.location &&
                          (locationDetails.location.subLocations.length > 0 ? (
                            locationDetails.location.subLocations.map(obj => (
                              <Option key={obj.id} value={obj.id}>
                                {locationDetails.location.nameEn} - {obj.nameEn}
                              </Option>
                            ))
                          ) : (
                            <Option
                              key={locationDetails.location.pk}
                              value={locationDetails.location.pk}
                            >
                              {locationDetails.location.nameEn}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[8, 8]}>
                  <Col md={12} xs={24}>
                    <Form.Item
                      label="Assigned Shift"
                      name="assigned_shift"
                      rules={[{ required: true }]}
                      initialValue=""
                    >
                      <Select>
                        <Option key="" value="">
                          -----
                        </Option>
                        {!shiftsListLoading &&
                          shiftsListData?.shifts?.edges.map(edge => (
                            <Option key={edge.node.pk} value={edge.node.pk}>
                              {`${moment(
                                edge.node.startingTime,
                                'HH:mm:ss',
                              ).format('hh:mm A')} - ${moment(
                                edge.node.endingTime,
                                'HH:mm:ss',
                              ).format('hh:mm A')} | ${moment(
                                edge.node.startingDate,
                              ).format('DD/MM/YYYY')} - ${moment(
                                edge.node.endingDate,
                              ).format('DD/MM/YYYY')}`}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col md={12} xs={24}>
                    <Form.Item
                      label="Assigned Role"
                      name="assigned_role"
                      rules={[{ required: true }]}
                      initialValue=""
                    >
                      <Select>
                        <Option key="" value="">
                          -----
                        </Option>
                        {!rolesListLoading &&
                          rolesListData?.roles?.edges.map(edge => (
                            <Option key={edge.node.pk} value={edge.node.pk}>
                              {edge.node.title}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[8, 8]}>
                  <Col xs={24}>
                    <Form.Item label="Days Off" name="days_off">
                      <Checkbox.Group>
                        <Checkbox value="Sunday" style={{ lineHeight: '32px' }}>
                          Sunday
                        </Checkbox>

                        <Checkbox value="Monday" style={{ lineHeight: '32px' }}>
                          Monday
                        </Checkbox>

                        <Checkbox
                          value="Tuesday"
                          style={{ lineHeight: '32px' }}
                        >
                          Tuesday
                        </Checkbox>

                        <Checkbox
                          value="Wednesday"
                          style={{ lineHeight: '32px' }}
                        >
                          Wednesday
                        </Checkbox>

                        <Checkbox
                          value="Thursday"
                          style={{ lineHeight: '32px' }}
                        >
                          Thursday
                        </Checkbox>

                        <Checkbox value="Friday" style={{ lineHeight: '32px' }}>
                          Friday
                        </Checkbox>

                        <Checkbox
                          value="Saturday "
                          style={{ lineHeight: '32px' }}
                        >
                          Saturday
                        </Checkbox>
                      </Checkbox.Group>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[8, 8]}>
                  <Col xs={24}>
                    <Form.Item
                      label="Potential Allocation"
                      name="potential_allocation"
                    >
                      <Checkbox.Group>
                        <Checkbox value="kiddie_rides">Kiddie Rides</Checkbox>
                        <Checkbox value="family_rides">Family Rides</Checkbox>
                        <Checkbox value="thrill_rides">Thrill Rides</Checkbox>
                      </Checkbox.Group>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[8, 8]}>
                  <Col xs={24}>
                    <Form.Item label="Comments" name="comments">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Submit Evaluation
          </Button>
        </Form.Item>
      </Form>
    </ResultsFormStyles>
  );
};

export default AddResultsForm;

const verticalGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
